import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_CARD, BG_SURFACE } from '../legacy-ball/table-management/designTokens';

interface Assignment { id: string; memberName: string; role: string | null; memberId: string | null; }
interface Step { id: string; title: string; description: string; status: string; sortOrder: number; assignments: Assignment[]; }
interface Props { workflowId: string; workflowName: string; canEdit: boolean; onBack: () => void; }
interface MemberResult { id: string; fullName: string; }

function useIsNarrow(breakpoint = 768): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' ? window.innerWidth < breakpoint : false);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches);
    setNarrow(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);
  return narrow;
}

const STATUS_OPTS = [
  { value: 'pending', label: 'Not Started', color: '#6b7280', bg: '#f3f4f6' },
  { value: 'active', label: 'In Progress', color: '#d97706', bg: '#fffbeb' },
  { value: 'completed', label: 'Ready', color: '#059669', bg: '#ecfdf5' },
];

function MemberAssignInput({ stepId, onAssigned }: { stepId: string; onAssigned: () => void }) {
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [results, setResults] = useState<MemberResult[]>([]);
  const [showDrop, setShowDrop] = useState(false);
  const [searching, setSearching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query.length < 2) { setResults([]); setShowDrop(false); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const d = await apiClient<MemberResult[]>('/users/members?search=' + encodeURIComponent(query));
        setResults(d || []);
        setShowDrop(true);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
  }, [query]);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setShowDrop(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const assign = async (name: string, memberId: string | null) => {
    await apiClient('/workflows/steps/' + stepId + '/assignments', { method: 'POST', body: { memberName: name, memberId, role: role.trim() || null } }).catch(() => {});
    setQuery(''); setRole(''); setShowDrop(false); onAssigned();
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="text" placeholder="Search member or type name..." value={query} onChange={e => setQuery(e.target.value)} onFocus={() => { if (results.length > 0) setShowDrop(true); }}
          style={{ padding: '7px 10px', border: '1px solid ' + BORDER_LIGHT, borderRadius: '6px', fontSize: '0.82rem', width: '180px' }} />
        <input type="text" placeholder="Role" value={role} onChange={e => setRole(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid ' + BORDER_LIGHT, borderRadius: '6px', fontSize: '0.82rem', width: '100px' }} />
      </div>
      {showDrop && (
        <div style={{ position: 'absolute', top: '38px', left: 0, width: '280px', maxHeight: '200px', overflowY: 'auto', backgroundColor: '#fff', border: '1px solid ' + BORDER_LIGHT, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10 }}>
          {searching && <div style={{ padding: '10px', fontSize: '0.8rem', color: TEXT_MUTED }}>Searching...</div>}
          {!searching && results.map(m => (
            <div key={m.id} onClick={() => assign(m.fullName, m.id)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.82rem', color: TEXT_DARK, borderBottom: '1px solid #f3f4f6' }}
              onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f0fdf4')}
              onMouseOut={e => (e.currentTarget.style.backgroundColor = '#fff')}>
              <span style={{ fontWeight: 600 }}>{m.fullName}</span>
              <span style={{ marginLeft: '6px', fontSize: '0.7rem', color: PRIMARY }}>✓ Member</span>
            </div>
          ))}
          {!searching && query.length >= 2 && (
            <div onClick={() => assign(query.trim(), null)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.82rem', color: '#6d28d9', borderTop: results.length > 0 ? '1px solid ' + BORDER_LIGHT : 'none', backgroundColor: '#faf5ff' }}
              onMouseOver={e => (e.currentTarget.style.backgroundColor = '#ede9fe')}
              onMouseOut={e => (e.currentTarget.style.backgroundColor = '#faf5ff')}>
              + Add &quot;{query.trim()}&quot; as volunteer
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function WorkflowStepsMatrix({ workflowId, workflowName, canEdit, onBack }: Props) {
  const [steps, setSteps] = useState<Step[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesVal, setNotesVal] = useState('');
  const [newTitle, setNewTitle] = useState('');

  const fetchSteps = useCallback(async () => {
    try { const d = await apiClient<Step[]>('/workflows/' + workflowId + '/steps'); setSteps(d || []); }
    catch { setSteps([]); }
    finally { setLoading(false); }
  }, [workflowId]);
  useEffect(() => { fetchSteps(); }, [fetchSteps]);

  const selectedStep = steps.find(s => s.id === selectedId) || null;
  const getSI = (s: string) => STATUS_OPTS.find(x => x.value === s) || STATUS_OPTS[0];

  const addStep = async () => {
    if (!newTitle.trim()) return;
    try { await apiClient('/workflows/' + workflowId + '/steps', { method: 'POST', body: { title: newTitle.trim() } }); setNewTitle(''); await fetchSteps(); }
    catch (e: any) { alert('Failed: ' + (e?.message || 'Unknown')); }
  };
  const updateStep = async (id: string, d: any) => { await apiClient('/workflows/steps/' + id, { method: 'PATCH', body: d }).catch(() => {}); fetchSteps(); };
  const deleteStep = async (id: string) => { if (!confirm('Delete?')) return; await apiClient('/workflows/steps/' + id, { method: 'DELETE' }).catch(() => {}); setSelectedId(null); fetchSteps(); };
  const saveNotes = async (id: string) => { await updateStep(id, { description: notesVal }); setEditingNotes(false); };
  const removeAssign = async (id: string) => { await apiClient('/workflows/assignments/' + id, { method: 'DELETE' }).catch(() => {}); fetchSteps(); };

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: TEXT_MUTED }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: '1rem', cursor: 'pointer', color: PRIMARY, fontWeight: 600 }}>&larr; Back</button>
        <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, margin: 0 }}>{workflowName} &mdash; Fulfillment Matrix</h2>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {STATUS_OPTS.map(s => <div key={s.value} style={{ padding: '4px 10px', borderRadius: '14px', backgroundColor: s.bg, color: s.color, fontSize: '0.75rem', fontWeight: 600 }}>{s.label}: {steps.filter(st => st.status === s.value).length}</div>)}
        <div style={{ padding: '4px 10px', borderRadius: '14px', backgroundColor: '#ede9fe', color: '#6d28d9', fontSize: '0.75rem', fontWeight: 600 }}>Total: {steps.length}</div>
      </div>

      <div style={{ display: 'flex', gap: '1px', border: '1px solid ' + BORDER_LIGHT, borderRadius: '12px', overflow: 'hidden', minHeight: '400px', backgroundColor: BORDER_LIGHT }}>
        <div style={{ width: '280px', minWidth: '280px', backgroundColor: BG_CARD, overflowY: 'auto', maxHeight: '600px' }}>
          {steps.map(step => {
            const si = getSI(step.status); const sel = selectedId === step.id;
            return (
              <div key={step.id} onClick={() => { setSelectedId(step.id); setEditingNotes(false); }}
                style={{ padding: '12px 14px', cursor: 'pointer', borderBottom: '1px solid ' + BORDER_LIGHT, backgroundColor: sel ? BG_SURFACE : BG_CARD, borderLeft: sel ? '3px solid ' + PRIMARY : '3px solid transparent' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '0.65rem', padding: '2px 6px', borderRadius: '8px', backgroundColor: si.bg, color: si.color, fontWeight: 600 }}>{si.label}</span>
                  {step.assignments.length > 0 && <span style={{ fontSize: '0.65rem', color: '#6d28d9' }}>{step.assignments.length} assigned</span>}
                </div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK }}>{step.title}</div>
              </div>
            );
          })}
          {canEdit && (
            <div style={{ padding: '12px 14px' }}>
              <input type="text" placeholder="+ New step..." value={newTitle} onChange={e => setNewTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addStep(); }}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid ' + BORDER_LIGHT, borderRadius: '6px', fontSize: '0.8rem', boxSizing: 'border-box' }} />
              {newTitle.trim() && <button onClick={addStep} style={{ marginTop: '6px', width: '100%', padding: '6px', backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>+ Add Step</button>}
            </div>
          )}
          {steps.length === 0 && <div style={{ padding: '2rem 1rem', textAlign: 'center', color: TEXT_MUTED, fontSize: '0.82rem' }}>No steps yet.</div>}
        </div>

        <div style={{ flex: 1, backgroundColor: BG_CARD, padding: '1.25rem', overflowY: 'auto', maxHeight: '600px' }}>
          {!selectedStep ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: TEXT_MUTED, fontSize: '0.9rem' }}>Select a step to view details</div>
          ) : (
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: TEXT_DARK, margin: '0 0 1rem' }}>{selectedStep.title}</h3>
              {canEdit && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', marginBottom: '0.4rem' }}>Status</div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {STATUS_OPTS.map(s => <button key={s.value} onClick={() => updateStep(selectedStep.id, { status: s.value })}
                      style={{ padding: '6px 12px', fontSize: '0.78rem', fontWeight: 600, borderRadius: '14px', border: 'none', cursor: 'pointer', backgroundColor: selectedStep.status === s.value ? s.color : '#e5e7eb', color: selectedStep.status === s.value ? '#fff' : '#374151' }}>{s.label}</button>)}
                  </div>
                </div>
              )}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', marginBottom: '0.4rem' }}>Fulfillment Notes</div>
                {editingNotes ? (
                  <div>
                    <textarea value={notesVal} onChange={e => setNotesVal(e.target.value)} style={{ width: '100%', minHeight: '100px', padding: '0.6rem', border: '1px solid ' + BORDER_LIGHT, borderRadius: '8px', fontSize: '0.85rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} placeholder="How will the team fulfill this step?" />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <button onClick={() => saveNotes(selectedStep.id)} style={{ padding: '6px 14px', fontSize: '0.78rem', fontWeight: 600, backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Save</button>
                      <button onClick={() => setEditingNotes(false)} style={{ padding: '6px 14px', fontSize: '0.78rem', fontWeight: 600, backgroundColor: '#e5e7eb', color: '#333', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div onClick={() => { if (canEdit) { setEditingNotes(true); setNotesVal(selectedStep.description); } }}
                    style={{ padding: '0.75rem', backgroundColor: BG_SURFACE, borderRadius: '8px', fontSize: '0.85rem', color: selectedStep.description ? TEXT_DARK : TEXT_MUTED, minHeight: '60px', cursor: canEdit ? 'pointer' : 'default', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {selectedStep.description || (canEdit ? 'Click to add notes...' : 'No notes yet.')}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', marginBottom: '0.4rem' }}>Assigned Volunteers / Delegates</div>
                {selectedStep.assignments.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginBottom: '0.6rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid ' + BORDER_LIGHT }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '0.7rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase' }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '0.7rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase' }}>Role</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '0.7rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase' }}>Type</th>
                        {canEdit && <th style={{ width: '30px' }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedStep.assignments.map(a => (
                        <tr key={a.id} style={{ borderBottom: '1px solid ' + BORDER_LIGHT }}>
                          <td style={{ padding: '8px', fontWeight: 600, color: TEXT_DARK }}>{a.memberName}</td>
                          <td style={{ padding: '8px', color: TEXT_MUTED }}>{a.role || '—'}</td>
                          <td style={{ padding: '8px' }}>
                            <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: '10px', backgroundColor: a.memberId ? '#e8f5e9' : '#ede9fe', color: a.memberId ? '#1a6b3c' : '#6d28d9', fontWeight: 600 }}>
                              {a.memberId ? '✓ Member' : 'Volunteer'}
                            </span>
                          </td>
                          {canEdit && (
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <button onClick={() => removeAssign(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: '#dc2626', padding: 0 }} title="Remove">&times;</button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {canEdit && <MemberAssignInput stepId={selectedStep.id} onAssigned={fetchSteps} />}
                {selectedStep.assignments.length === 0 && !canEdit && <div style={{ fontSize: '0.82rem', color: TEXT_MUTED }}>No assignments yet.</div>}
              </div>
              {canEdit && (
                <div style={{ borderTop: '1px solid ' + BORDER_LIGHT, paddingTop: '1rem' }}>
                  <button onClick={() => deleteStep(selectedStep.id)} style={{ background: 'none', border: '1px solid #fecaca', color: '#dc2626', fontSize: '0.78rem', fontWeight: 500, padding: '6px 14px', borderRadius: '6px', cursor: 'pointer' }}>Delete Step</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
